import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, CircularProgress, Alert, Stack,
  TextField, Switch, FormControlLabel, Button, Divider, Chip, Grid,
  Card, CardContent, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableRow, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Slider, Tooltip, IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { adminEndpoints } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function AdminSettingsPage() {
  const { getAccessToken, user } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // All settings data
  const [appSettings, setAppSettings] = useState(null);
  const [cognitoSettings, setCognitoSettings] = useState(null);
  const [apiGatewaySettings, setApiGatewaySettings] = useState(null);
  const [systemInfo, setSystemInfo] = useState(null);

  // Editable form state
  const [appForm, setAppForm] = useState({});
  const [cognitoForm, setCognitoForm] = useState({});
  const [appDirty, setAppDirty] = useState(false);
  const [cognitoDirty, setCognitoDirty] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchAllSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(adminEndpoints.settings, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load settings');
      const data = await response.json();

      setAppSettings(data.app);
      setCognitoSettings(data.cognito);
      setApiGatewaySettings(data.apigateway);
      setSystemInfo(data.system);

      // Initialize form state
      setAppForm({
        maintenanceMode: data.app?.maintenanceMode ?? false,
        maxApiKeysPerUser: data.app?.maxApiKeysPerUser ?? 1,
        autoApproveApiKeys: data.app?.autoApproveApiKeys ?? true,
        allowedDomains: data.app?.allowedDomains ?? '',
        notificationEmail: data.app?.notificationEmail ?? '',
        defaultUsagePlanId: data.app?.defaultUsagePlanId ?? '',
        apiKeyExpirationDays: data.app?.apiKeyExpirationDays ?? 0,
        requireEmailVerification: data.app?.requireEmailVerification ?? true,
        maxUsersLimit: data.app?.maxUsersLimit ?? 0,
        rateLimitWarningThreshold: data.app?.rateLimitWarningThreshold ?? 80,
        enableApiKeyRotation: data.app?.enableApiKeyRotation ?? false,
        apiKeyRotationDays: data.app?.apiKeyRotationDays ?? 90,
        enableAuditLog: data.app?.enableAuditLog ?? true,
        allowSelfRegistration: data.app?.allowSelfRegistration ?? true,
        sessionTimeoutMinutes: data.app?.sessionTimeoutMinutes ?? 60,
      });

      setCognitoForm({
        minimumLength: data.cognito?.passwordPolicy?.minimumLength ?? 8,
        requireLowercase: data.cognito?.passwordPolicy?.requireLowercase ?? true,
        requireUppercase: data.cognito?.passwordPolicy?.requireUppercase ?? true,
        requireNumbers: data.cognito?.passwordPolicy?.requireNumbers ?? true,
        requireSymbols: data.cognito?.passwordPolicy?.requireSymbols ?? false,
        temporaryPasswordValidityDays: data.cognito?.passwordPolicy?.temporaryPasswordValidityDays ?? 7,
      });

      setAppDirty(false);
      setCognitoDirty(false);
    } catch (err) {
      showSnackbar(err.message || 'Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => { fetchAllSettings(); }, [fetchAllSettings]);

  // ---- Save Handlers ----
  const handleSaveAppSettings = async () => {
    setSaving(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${adminEndpoints.settings}?section=app`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...appForm, _updatedBy: user?.email || 'admin' })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save settings');
      setAppSettings(data.settings);
      setAppDirty(false);
      showSnackbar('Application settings saved successfully');
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCognitoSettings = async () => {
    setSaving(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${adminEndpoints.settings}?section=cognito`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ passwordPolicy: cognitoForm })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save Cognito settings');
      setCognitoDirty(false);
      showSnackbar('Cognito password policy updated successfully');
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- App Form Helpers ----
  const updateAppField = (field, value) => {
    setAppForm(prev => ({ ...prev, [field]: value }));
    setAppDirty(true);
  };

  const updateCognitoField = (field, value) => {
    setCognitoForm(prev => ({ ...prev, [field]: value }));
    setCognitoDirty(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box maxWidth={960} mx="auto" mt={4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Admin Settings</Typography>
        <Tooltip title="Refresh all settings">
          <IconButton onClick={fetchAllSettings} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<SettingsIcon />} iconPosition="start" label="Application" />
          <Tab icon={<SecurityIcon />} iconPosition="start" label="Security & Auth" />
          <Tab icon={<CloudIcon />} iconPosition="start" label="API Gateway" />
          <Tab icon={<InfoIcon />} iconPosition="start" label="System Info" />
        </Tabs>

        {/* ========== TAB 0: Application Settings ========== */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Application Configuration</Typography>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveAppSettings}
                disabled={!appDirty || saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Stack>
            {appSettings?.updatedAt && (
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Last updated: {new Date(appSettings.updatedAt).toLocaleString()} by {appSettings.updatedBy || 'unknown'}
              </Typography>
            )}

            {/* General Settings */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">General Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appForm.maintenanceMode}
                          onChange={(e) => updateAppField('maintenanceMode', e.target.checked)}
                          color="warning"
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>Maintenance Mode</span>
                          {appForm.maintenanceMode && <Chip label="ACTIVE" color="warning" size="small" />}
                        </Stack>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                      When enabled, only admins can access the platform. Users will see a maintenance message.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appForm.allowSelfRegistration}
                          onChange={(e) => updateAppField('allowSelfRegistration', e.target.checked)}
                        />
                      }
                      label="Allow Self-Registration"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                      Allow new users to register themselves. When disabled, only admins can create accounts.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max Users Limit"
                      type="number"
                      fullWidth
                      value={appForm.maxUsersLimit}
                      onChange={(e) => updateAppField('maxUsersLimit', parseInt(e.target.value) || 0)}
                      helperText="Maximum number of users allowed (0 = unlimited)"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Session Timeout (minutes)"
                      type="number"
                      fullWidth
                      value={appForm.sessionTimeoutMinutes}
                      onChange={(e) => updateAppField('sessionTimeoutMinutes', parseInt(e.target.value) || 60)}
                      helperText="Idle session timeout in minutes (minimum 5)"
                      inputProps={{ min: 5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notification Email"
                      fullWidth
                      value={appForm.notificationEmail}
                      onChange={(e) => updateAppField('notificationEmail', e.target.value)}
                      helperText="Email address for system notifications and alerts"
                      placeholder="admin@example.com"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Allowed Domains"
                      fullWidth
                      value={appForm.allowedDomains}
                      onChange={(e) => updateAppField('allowedDomains', e.target.value)}
                      helperText="Comma-separated list of allowed email domains for registration (empty = all domains)"
                      placeholder="company.com, partner.org"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* API Key Settings */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">API Key Management</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max API Keys per User"
                      type="number"
                      fullWidth
                      value={appForm.maxApiKeysPerUser}
                      onChange={(e) => updateAppField('maxApiKeysPerUser', parseInt(e.target.value) || 1)}
                      helperText="Maximum number of API keys each user can have"
                      inputProps={{ min: 1, max: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="API Key Expiration (days)"
                      type="number"
                      fullWidth
                      value={appForm.apiKeyExpirationDays}
                      onChange={(e) => updateAppField('apiKeyExpirationDays', parseInt(e.target.value) || 0)}
                      helperText="Days until API keys expire (0 = never)"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appForm.autoApproveApiKeys}
                          onChange={(e) => updateAppField('autoApproveApiKeys', e.target.checked)}
                        />
                      }
                      label="Auto-Approve API Key Requests"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                      When enabled, API key requests are approved instantly. When disabled, admin approval is required.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appForm.enableApiKeyRotation}
                          onChange={(e) => updateAppField('enableApiKeyRotation', e.target.checked)}
                        />
                      }
                      label="Enable API Key Rotation"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                      Automatically remind users to rotate their API keys.
                    </Typography>
                  </Grid>
                  {appForm.enableApiKeyRotation && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Rotation Period (days)"
                        type="number"
                        fullWidth
                        value={appForm.apiKeyRotationDays}
                        onChange={(e) => updateAppField('apiKeyRotationDays', parseInt(e.target.value) || 90)}
                        helperText="Number of days between rotation reminders"
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Default Usage Plan ID"
                      fullWidth
                      value={appForm.defaultUsagePlanId}
                      onChange={(e) => updateAppField('defaultUsagePlanId', e.target.value)}
                      helperText="Default usage plan assigned to new API keys (leave empty for user choice)"
                      placeholder="abc123"
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Monitoring Settings */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Monitoring & Logging</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appForm.enableAuditLog}
                          onChange={(e) => updateAppField('enableAuditLog', e.target.checked)}
                        />
                      }
                      label="Enable Audit Logging"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                      Log all admin actions and API key operations for compliance.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appForm.requireEmailVerification}
                          onChange={(e) => updateAppField('requireEmailVerification', e.target.checked)}
                        />
                      }
                      label="Require Email Verification"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                      Users must verify their email address before accessing the platform.
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography gutterBottom>
                      Rate Limit Warning Threshold: <strong>{appForm.rateLimitWarningThreshold}%</strong>
                    </Typography>
                    <Slider
                      value={appForm.rateLimitWarningThreshold}
                      onChange={(_, v) => updateAppField('rateLimitWarningThreshold', v)}
                      min={0}
                      max={100}
                      step={5}
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 50, label: '50%' },
                        { value: 80, label: '80%' },
                        { value: 100, label: '100%' }
                      ]}
                      valueLabelDisplay="auto"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Send warnings when users reach this percentage of their rate limit quota.
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>

        {/* ========== TAB 1: Security & Auth (Cognito) ========== */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Security & Authentication</Typography>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveCognitoSettings}
                disabled={!cognitoDirty || saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Stack>

            {/* User Pool Overview */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Cognito User Pool Overview</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {cognitoSettings?.error ? (
                  <Alert severity="error">{cognitoSettings.error}</Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>User Pool ID</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{cognitoSettings?.userPoolId}</span>
                              <IconButton size="small" onClick={() => copyToClipboard(cognitoSettings?.userPoolId)}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>User Pool Name</TableCell>
                          <TableCell>{cognitoSettings?.userPoolName}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell>
                            <Chip
                              label={cognitoSettings?.status || 'Unknown'}
                              color={cognitoSettings?.status === 'Active' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Estimated Users</TableCell>
                          <TableCell>{cognitoSettings?.estimatedNumberOfUsers ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>MFA Configuration</TableCell>
                          <TableCell>
                            <Chip
                              label={cognitoSettings?.mfaConfiguration || 'OFF'}
                              color={cognitoSettings?.mfaConfiguration === 'ON' ? 'success' : cognitoSettings?.mfaConfiguration === 'OPTIONAL' ? 'warning' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Deletion Protection</TableCell>
                          <TableCell>
                            <Chip
                              label={cognitoSettings?.deletionProtection || 'INACTIVE'}
                              color={cognitoSettings?.deletionProtection === 'ACTIVE' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Auto-Verified Attributes</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              {cognitoSettings?.autoVerifiedAttributes?.map(attr => (
                                <Chip key={attr} label={attr} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Username Attributes</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              {cognitoSettings?.usernameAttributes?.map(attr => (
                                <Chip key={attr} label={attr} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                          <TableCell>{cognitoSettings?.creationDate ? new Date(cognitoSettings.creationDate).toLocaleString() : 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Last Modified</TableCell>
                          <TableCell>{cognitoSettings?.lastModifiedDate ? new Date(cognitoSettings.lastModifiedDate).toLocaleString() : 'N/A'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Password Policy */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight="bold">Password Policy</Typography>
                  {cognitoDirty && <Chip label="Modified" color="warning" size="small" />}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Changes to password policy apply to new users and password resets. Existing passwords are not affected.
                </Alert>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Minimum Password Length"
                      type="number"
                      fullWidth
                      value={cognitoForm.minimumLength}
                      onChange={(e) => updateCognitoField('minimumLength', parseInt(e.target.value) || 8)}
                      helperText="Between 6 and 99 characters"
                      inputProps={{ min: 6, max: 99 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Temporary Password Validity (days)"
                      type="number"
                      fullWidth
                      value={cognitoForm.temporaryPasswordValidityDays}
                      onChange={(e) => updateCognitoField('temporaryPasswordValidityDays', parseInt(e.target.value) || 7)}
                      helperText="Days before temporary passwords expire (1-365)"
                      inputProps={{ min: 1, max: 365 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cognitoForm.requireLowercase}
                          onChange={(e) => updateCognitoField('requireLowercase', e.target.checked)}
                        />
                      }
                      label="Require Lowercase Letters"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cognitoForm.requireUppercase}
                          onChange={(e) => updateCognitoField('requireUppercase', e.target.checked)}
                        />
                      }
                      label="Require Uppercase Letters"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cognitoForm.requireNumbers}
                          onChange={(e) => updateCognitoField('requireNumbers', e.target.checked)}
                        />
                      }
                      label="Require Numbers"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cognitoForm.requireSymbols}
                          onChange={(e) => updateCognitoField('requireSymbols', e.target.checked)}
                        />
                      }
                      label="Require Symbols"
                    />
                  </Grid>
                </Grid>

                {/* Password Strength Preview */}
                <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Password Requirements Preview</Typography>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="body2">At least {cognitoForm.minimumLength} characters</Typography>
                    </Stack>
                    {cognitoForm.requireLowercase && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon fontSize="small" color="success" />
                        <Typography variant="body2">Contains lowercase letter (a-z)</Typography>
                      </Stack>
                    )}
                    {cognitoForm.requireUppercase && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon fontSize="small" color="success" />
                        <Typography variant="body2">Contains uppercase letter (A-Z)</Typography>
                      </Stack>
                    )}
                    {cognitoForm.requireNumbers && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon fontSize="small" color="success" />
                        <Typography variant="body2">Contains number (0-9)</Typography>
                      </Stack>
                    )}
                    {cognitoForm.requireSymbols && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon fontSize="small" color="success" />
                        <Typography variant="body2">Contains symbol (!@#$%^&*)</Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {/* Account Recovery */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Account Recovery</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Account recovery mechanisms configured in this user pool (read-only, managed via CloudFormation):
                </Typography>
                {cognitoSettings?.accountRecoverySetting?.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {cognitoSettings.accountRecoverySetting.map((mechanism, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Priority {mechanism.priority}</TableCell>
                            <TableCell>{mechanism.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2">Default account recovery (email-based).</Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>

        {/* ========== TAB 2: API Gateway ========== */}
        <TabPanel value={tab} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>API Gateway Overview</Typography>

            {apiGatewaySettings?.error ? (
              <Alert severity="error">{apiGatewaySettings.error}</Alert>
            ) : (
              <>
                {/* Summary Cards */}
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary">{apiGatewaySettings?.totalApis ?? 0}</Typography>
                        <Typography variant="body2" color="text.secondary">REST APIs</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary">{apiGatewaySettings?.totalUsagePlans ?? 0}</Typography>
                        <Typography variant="body2" color="text.secondary">Usage Plans</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" color="primary">{apiGatewaySettings?.totalApiKeys ?? 0}</Typography>
                        <Typography variant="body2" color="text.secondary">API Keys</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* APIs List */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">REST APIs</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {apiGatewaySettings?.apis?.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {apiGatewaySettings.apis.map(api => (
                              <TableRow key={api.id}>
                                <TableCell sx={{ fontWeight: 'bold' }}>{api.name}</TableCell>
                                <TableCell>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2" color="text.secondary">{api.id}</Typography>
                                    <IconButton size="small" onClick={() => copyToClipboard(api.id)}>
                                      <CopyIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </TableCell>
                                <TableCell>{api.description || '-'}</TableCell>
                                <TableCell>{api.createdDate ? new Date(api.createdDate).toLocaleDateString() : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No REST APIs found.</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Stages */}
                {apiGatewaySettings?.stages?.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">API Stages</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableBody>
                            {apiGatewaySettings.stages.map(stage => (
                              <TableRow key={stage.stageName}>
                                <TableCell sx={{ fontWeight: 'bold' }}>{stage.stageName}</TableCell>
                                <TableCell>Deployment: {stage.deploymentId}</TableCell>
                                <TableCell>
                                  Cache: <Chip label={stage.cacheClusterEnabled ? 'Enabled' : 'Disabled'} size="small" color={stage.cacheClusterEnabled ? 'success' : 'default'} />
                                </TableCell>
                                <TableCell>
                                  {stage.throttleSettings ? (
                                    <Typography variant="body2">
                                      Rate: {stage.throttleSettings.rateLimit} req/s, Burst: {stage.throttleSettings.burstLimit}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">Default throttle</Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Usage Plans */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">Usage Plans</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {apiGatewaySettings?.usagePlans?.length > 0 ? (
                      <Stack spacing={2}>
                        {apiGatewaySettings.usagePlans.map(plan => (
                          <Paper key={plan.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography fontWeight="bold">{plan.name}</Typography>
                              <Chip label={plan.id} size="small" variant="outlined" />
                            </Stack>
                            {plan.description && (
                              <Typography variant="body2" color="text.secondary" mt={0.5}>{plan.description}</Typography>
                            )}
                            <Grid container spacing={2} mt={1}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Quota</Typography>
                                <Typography variant="body2">
                                  {plan.quota ? `${plan.quota.limit} requests / ${plan.quota.period}` : 'Unlimited'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Throttle</Typography>
                                <Typography variant="body2">
                                  {plan.throttle ? `${plan.throttle.rateLimit} req/s (burst: ${plan.throttle.burstLimit})` : 'No limit'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No usage plans configured.</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </>
            )}
          </Box>
        </TabPanel>

        {/* ========== TAB 3: System Info ========== */}
        <TabPanel value={tab} index={3}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>System Information</Typography>

            {systemInfo?.error ? (
              <Alert severity="error">{systemInfo.error}</Alert>
            ) : (
              <>
                {/* Environment */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">Environment</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>AWS Region</TableCell>
                            <TableCell>{systemInfo?.environment?.region}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>User Pool ID</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <span>{systemInfo?.environment?.userPoolId}</span>
                                <IconButton size="small" onClick={() => copyToClipboard(systemInfo?.environment?.userPoolId)}>
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Settings Table</TableCell>
                            <TableCell>{systemInfo?.environment?.settingsTable}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>User Table</TableCell>
                            <TableCell>{systemInfo?.environment?.userTable}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>VAT API ID</TableCell>
                            <TableCell>{systemInfo?.environment?.vatApiId}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Node.js Version</TableCell>
                            <TableCell>{systemInfo?.environment?.nodeVersion}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Lambda Function</TableCell>
                            <TableCell>{systemInfo?.environment?.lambdaFunctionName}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Lambda Memory</TableCell>
                            <TableCell>{systemInfo?.environment?.lambdaMemory} MB</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Log Group</TableCell>
                            <TableCell>{systemInfo?.environment?.lambdaLogGroup}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                {/* Statistics */}
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold">Statistics</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="primary">
                              {systemInfo?.statistics?.totalRegisteredUsers ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Registered Users (DynamoDB)</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="primary">
                              {cognitoSettings?.estimatedNumberOfUsers ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Cognito Users</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h3" color="primary">
                              {apiGatewaySettings?.totalApiKeys ?? 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Active API Keys</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Timestamp */}
                <Typography variant="caption" color="text.secondary" display="block" mt={2} textAlign="right">
                  Data fetched at: {systemInfo?.timestamp ? new Date(systemInfo.timestamp).toLocaleString() : 'N/A'}
                </Typography>
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
